import { useMemo, useState, type ReactNode } from "react";
import { cx } from "@/components/ui";
import { IosChevron } from "@/components/mobile/MobileUi";
import { eur2, num } from "@/lib/format";
import { LevelBadge, PCard, coverage, stockLevel, type StockLevel } from "./Kit";

/** Ligne commune au stock d'une boutique et au catalogue du périmètre. */
export interface StockTableRow {
  key: string;
  /** Identifiant interne du produit (produit réseau), pour ouvrir sa fiche. */
  id: string | null;
  name: string;
  category: string | null;
  stock: number;
  sold30: number;
  coverageDays: number | null;
  price: number | null;
  supplyPrice: number | null;
  promo: { label: string } | null;
  /** Catalogue : nombre de boutiques en rupture. */
  boutiquesRupture?: number;
  /** Statut imposé (catalogue : rupture si une boutique l'est). */
  level?: StockLevel;
}

type SortKey = "name" | "stock" | "sold30" | "coverageDays" | "price" | "boutiquesRupture";
const PAGE = 100;

export const levelOf = (r: StockTableRow) => r.level ?? stockLevel(r.stock, r.coverageDays);

/**
 * Tableau produits (PC) : Produit · Stock · Vendu 30 j · Couverture · Achat → vente
 * (boutique) ou En rupture (catalogue) · Statut · › ; tri au clic sur l'en-tête.
 */
export function StockTable({
  rows,
  mode,
  stockLabel = "Stock",
  onOpen,
}: {
  rows: StockTableRow[];
  mode: "boutique" | "catalogue";
  stockLabel?: string;
  onOpen: (r: StockTableRow) => void;
}) {
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "sold30", dir: -1 });
  const [shown, setShown] = useState(PAGE);
  const sorted = useMemo(() => {
    const v = (r: StockTableRow) => (sort.key === "name" ? r.name : sort.key === "price" ? r.price : (r[sort.key] ?? null));
    return [...rows].sort((a, b) => {
      const x = v(a);
      const y = v(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      const c = typeof x === "string" && typeof y === "string" ? x.localeCompare(y, "fr") : Number(x) - Number(y);
      return c * sort.dir || a.name.localeCompare(b.name, "fr");
    });
  }, [rows, sort]);

  const head = (key: SortKey, label: ReactNode, align: "left" | "right" = "right") => (
    <th className={cx("px-4 py-[10px] text-[12px] font-medium text-muted", align === "right" ? "text-right" : "text-left")}>
      <button
        type="button"
        onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : key === "name" ? 1 : -1 }))}
        className={cx("hover:text-ink", sort.key === key && "text-ink")}
      >
        {label}
        {sort.key === key ? (sort.dir === 1 ? " ↑" : " ↓") : ""}
      </button>
    </th>
  );

  if (rows.length === 0) return <PCard className="px-4 py-8 text-center text-[15px] text-muted">Aucun produit ne correspond.</PCard>;
  return (
    <PCard className="overflow-x-auto">
      <table className="w-full min-w-[760px] border-collapse">
        <thead>
          <tr className="border-b-[0.5px] border-ios-sep">
            {head("name", "Produit", "left")}
            {head("stock", stockLabel)}
            {head("sold30", "Vendu 30 j")}
            {head("coverageDays", "Couverture")}
            {mode === "boutique" ? head("price", "Achat → vente") : head("boutiquesRupture", "En rupture")}
            <th className="px-4 py-[10px] text-right text-[12px] font-medium text-muted">Statut</th>
            <th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {sorted.slice(0, shown).map((r) => (
            <tr key={r.key} onClick={() => onOpen(r)} className="cursor-pointer border-b-[0.5px] border-ios-sep last:border-b-0 hover:bg-[oklch(0.975_0.004_145)]">
              <td className="max-w-[360px] px-4 py-[9px]">
                <div className="flex items-center gap-2">
                  <span className="truncate text-[15px]">{r.name}</span>
                  {r.promo && <span className="flex-shrink-0 rounded-[6px] bg-accent px-[6px] py-px text-[11px] font-semibold text-white">{r.promo.label}</span>}
                </div>
                {r.category && <div className="truncate text-[12px] text-muted">{r.category}</div>}
              </td>
              <td className={cx("px-4 text-right text-[16px] font-bold tabular", r.stock <= 0 && "text-danger")}>{num(r.stock)}</td>
              <td className="px-4 text-right text-[14px] tabular">{num(r.sold30)}</td>
              <td className="px-4 text-right text-[14px] tabular">{coverage(r.coverageDays)}</td>
              {mode === "boutique" ? (
                <td className="whitespace-nowrap px-4 text-right text-[13px] tabular text-muted">
                  {eur2(r.supplyPrice)} → {eur2(r.price)}
                </td>
              ) : (
                <td className="whitespace-nowrap px-4 text-right text-[13px] text-muted">
                  {r.boutiquesRupture ? `${r.boutiquesRupture} boutique${r.boutiquesRupture > 1 ? "s" : ""}` : "—"}
                </td>
              )}
              <td className="px-4 text-right">
                <LevelBadge level={levelOf(r)} />
              </td>
              <td className="pr-3">
                <IosChevron />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sorted.length > shown && (
        <button type="button" onClick={() => setShown((s) => s + PAGE)} className="w-full border-t-[0.5px] border-ios-sep py-3 text-[15px] text-accent hover:bg-[oklch(0.975_0.004_145)]">
          Voir plus ({num(sorted.length - shown)})
        </button>
      )}
    </PCard>
  );
}
